import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { ErrorAlert } from '@/components/common/ErrorAlert';
import { PaymentSummaryCard } from './PaymentSummaryCard';
import { PaymentListTable } from './PaymentListTable';
import { PaymentEntryForm } from './PaymentEntryForm';
import { useJobPayments } from '../hooks/useJobPayments';
import { useJobPaymentSummary } from '../hooks/useJobPaymentSummary';
import { useJobChargeTotal } from '../hooks/useJobChargeTotal';
import { useDeleteJobPayment } from '../hooks/useDeleteJobPayment';
import { useClearPayment } from '../hooks/useClearPayment';
import type { JobPayment } from '../types/jobPayment';

interface JobPaymentsTabProps {
  jobId: number;
}

export function JobPaymentsTab({ jobId }: JobPaymentsTabProps) {
  const [isForecast, setIsForecast] = useState(false);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingPayment, setEditingPayment] = useState<JobPayment | null>(null);

  const {
    data: payments,
    isLoading: isLoadingPayments,
    error: paymentsError,
    refetch: refetchPayments,
  } = useJobPayments(jobId, isForecast);

  const {
    data: summary,
    isLoading: isLoadingSummary,
    error: summaryError,
    refetch: refetchSummary,
  } = useJobPaymentSummary(jobId, isForecast);

  const {
    data: chargeTotal,
    isLoading: isLoadingChargeTotal,
    error: chargeError,
  } = useJobChargeTotal(jobId, isForecast);

  const { deletePayment, isDeleting } = useDeleteJobPayment();
  const { clear, isClearing } = useClearPayment();

  const handleAddPayment = () => {
    setEditingPayment(null);
    setIsFormOpen(true);
  };

  const handleEditPayment = (payment: JobPayment) => {
    setEditingPayment(payment);
    setIsFormOpen(true);
  };

  const handleDeletePayment = async (paymentId: number) => {
    const success = await deletePayment(jobId, paymentId);
    if (success) {
      refetchPayments();
      refetchSummary();
    }
  };

  const handleClearPayment = async (paymentId: number) => {
    const result = await clear(jobId, paymentId);
    if (result) {
      refetchPayments();
      refetchSummary();
    }
  };

  const handleFormSuccess = () => {
    refetchPayments();
    refetchSummary();
  };

  const isLoading = isLoadingPayments || isLoadingSummary || isLoadingChargeTotal;
  const error = paymentsError || summaryError || chargeError;

  // Calculate outstanding: charges total - received payments
  const totalCharges = chargeTotal?.totalAmount ?? 0;
  const totalReceived = summary?.totalReceived ?? 0;
  const outstanding = totalCharges - totalReceived;

  return (
    <div className="p-4 space-y-4">
      {/* Header with toggle and add button */}
      <div className="flex justify-between items-center">
        <div className="flex gap-2">
          <Button
            variant={!isForecast ? 'default' : 'outline'}
            onClick={() => setIsForecast(false)}
            className={
              !isForecast
                ? 'bg-[#0A0A9E] text-white hover:bg-[#0A0A9E]/90'
                : 'text-gray-600 hover:bg-gray-100'
            }
          >
            Actual
          </Button>
          <Button
            variant={isForecast ? 'default' : 'outline'}
            onClick={() => setIsForecast(true)}
            className={
              isForecast
                ? 'bg-[#0A0A9E] text-white hover:bg-[#0A0A9E]/90'
                : 'text-gray-600 hover:bg-gray-100'
            }
          >
            Forecast
          </Button>
        </div>

        <Button variant="primary" onClick={handleAddPayment}>
          Add Payment
        </Button>
      </div>

      <ErrorAlert error={error} />

      {/* Loading state */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <LoadingSpinner size="lg" label="Loading payments..." />
        </div>
      ) : (
        <>
          {/* Payment Summary Card */}
          <PaymentSummaryCard
            totalReceived={totalReceived}
            totalPending={summary?.totalPending ?? 0}
            outstanding={outstanding}
          />

          {/* Payments table */}
          <PaymentListTable
            jobId={jobId}
            payments={payments}
            onEdit={handleEditPayment}
            onDelete={handleDeletePayment}
            onClear={handleClearPayment}
            isDeleting={isDeleting}
            isClearing={isClearing}
          />
        </>
      )}

      {/* Payment entry form dialog */}
      <PaymentEntryForm
        jobId={jobId}
        isOpen={isFormOpen}
        onClose={() => {
          setIsFormOpen(false);
          setEditingPayment(null);
        }}
        onSuccess={handleFormSuccess}
        editingPayment={editingPayment}
        isForecast={isForecast}
      />
    </div>
  );
}
